import React, { useMemo, useState } from "react";

import { ShareDialog } from "#app/components/sharing/share-dialog";
import { useAppData } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/app-provider";
import type { EntityData } from "#app/lib/entity-visuals";
import { useWorkflowSharing } from "#app/lib/hooks/use-workflow-sharing";
import { workflowHref } from "#app/lib/utils/entity-hrefs";

// Workflow-specific wrapper around the generic ShareDialog. Owns the
// debounced search → useWorkflowSharing wiring, the workflow's entity chip
// as the title, and the workflow's deep link.

type WorkflowShareDialogProps = {
  workflowId: string | null;
  onOpenChange: (open: boolean) => void;
};

export function WorkflowShareDialog({
  workflowId,
  onOpenChange,
}: WorkflowShareDialogProps) {
  const { workflows } = useAppData();
  const { activeWorkspace } = useWorkspace();
  const workflow = workflows.find((w) => w.workflowId === workflowId);
  const [activeSearch, setActiveSearch] = useState<string | undefined>(
    undefined,
  );

  const sharing = useWorkflowSharing(workflowId, activeSearch);
  const subject = useMemo(
    (): EntityData | null =>
      workflow
        ? {
            kind: "workflow",
            id: workflow.workflowId,
            workflow: { name: workflow.name, icon: workflow.icon },
          }
        : null,
    [workflow],
  );
  // `/workflow/:id` lands on the workflow's default view.
  const shareUrl =
    workflowId && activeWorkspace
      ? workflowHref(activeWorkspace.urlPath, workflowId)
      : null;

  return (
    <ShareDialog
      open={workflowId != null}
      onOpenChange={onOpenChange}
      subject={subject}
      subjectNoun="workflow"
      // A workflow's share covers every view in it.
      description="Sharing workflow with all views"
      shareUrl={shareUrl}
      sharing={sharing}
      onSearchChange={setActiveSearch}
    />
  );
}
